import { useState, useEffect, useMemo } from 'react';
import { type ActiveTab } from '../types';

const ACTIVE_TAB_STORAGE_KEY = 'activeTab';

function getStoredActiveTab(): ActiveTab {
    try {
        const stored = localStorage.getItem(ACTIVE_TAB_STORAGE_KEY);
        if (stored) {
            return stored as ActiveTab;
        }
    } catch {
        // localStorage may not be available
    }
    return null;
}

function storeActiveTab(tab: ActiveTab): void {
    try {
        if (tab) {
            localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, tab);
        }
    } catch {
        // localStorage may not be available
    }
}

function deriveActiveTabFromPath(path: string): ActiveTab {
    if (path.startsWith('/dashboard')) return 'dashboard';
    if (path.startsWith('/courses')) return 'courses';
    if (path.startsWith('/careers')) return 'careers';
    if (path.startsWith('/student-network')) return 'student-network';
    if (path.startsWith('/settings/application')) return 'application';
    if (path.startsWith('/resources')) return 'resources';
    if (path.startsWith('/bot')) return 'ai-advisor';
    return null;
}

export function useActiveTab(currentPath: string): ActiveTab {
    const derivedTab = useMemo(() => deriveActiveTabFromPath(currentPath), [currentPath]);

    const [activeTab, setActiveTab] = useState<ActiveTab>(
        () =>
            // Initialize from derived tab or stored value
            derivedTab ?? getStoredActiveTab(),
    );

    useEffect(() => {
        if (derivedTab) {
            setActiveTab(derivedTab);
            storeActiveTab(derivedTab);
        }
    }, [derivedTab]);

    return activeTab;
}
